import { inject, Injectable, Signal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { distinctUntilChanged, map } from "rxjs/operators";

export enum PageFilterQueryParam {
	NAME_DEPARTMENT = "pf_department",
	NAME_RESIDENT = "resident",
}

export enum PageFilterType {
	REQUIRED = "REQUIRED",
	ALLOWED = "ALLOWED",
	NOT_ALLOWED = "NOT_ALLOWED",
}

export interface PageFilterConfig {
	departmentFilter?: PageFilterType;
	residentFilter?: PageFilterType;
}

@Injectable({
	providedIn: "root",
})
export class PageFilterService {
	private readonly activatedRoute = inject(ActivatedRoute);

	private readonly _filterConfig = signal<PageFilterConfig>({
		departmentFilter: PageFilterType.NOT_ALLOWED,
		residentFilter: PageFilterType.NOT_ALLOWED,
	});

	public setFilterConfig(config: PageFilterConfig) {
		const currentConfig = this._filterConfig();
		if (
			currentConfig.departmentFilter !== config.departmentFilter
			|| currentConfig.residentFilter !== config.residentFilter
		) {
			this._filterConfig.update(() => ({ ...this._filterConfig(), ...config }));
		}
	}

	public getFilterConfigSignal(): Signal<PageFilterConfig> {
		return this._filterConfig;
	}

	public getValue(filterName: PageFilterQueryParam) {
		return toSignal(
			this.activatedRoute.queryParamMap.pipe(
				map((paramMap) => paramMap.get(filterName)),
				distinctUntilChanged(),
			),
			{ initialValue: null },
		);
	}

	public getValueOnce(filterName: PageFilterQueryParam) {
		return this.activatedRoute.snapshot.queryParamMap.get(filterName);
	}

	public resetFilterConfig() {
		this._filterConfig.set({
			departmentFilter: PageFilterType.NOT_ALLOWED,
			residentFilter: PageFilterType.NOT_ALLOWED,
		});
	}
}
